import type { Game } from "./types.js";
import { breakout } from "./breakout.js";
import { asteroids } from "./asteroids.js";
import { spaceInvaders } from "./space-invaders.js";

interface SceneHooks {
  pause: () => void;
  resume: () => void;
}

const DESKTOP_MQ = "(min-width: 1024px)";
const games: Game[] = [breakout, asteroids, spaceInvaders];

// Hidden until the player taps Space 3 times within 2 seconds.
const COMBO_WINDOW_MS = 2000;
const COMBO_PRESSES = 3;

export function mountLauncher(hooks: SceneHooks): void {
  const mq = window.matchMedia(DESKTOP_MQ);
  let launcherBtn: HTMLButtonElement | null = null;
  let launcherIconCleanup: (() => void) | null = null;
  let revealed = false;

  const renderButton = (): void => {
    if (launcherBtn) return;
    const btn = document.createElement("button");
    btn.className = "game-launcher";
    btn.type = "button";
    btn.setAttribute("aria-label", "Open game launcher");
    const iconCanvas = document.createElement("canvas");
    iconCanvas.className = "game-launcher__canvas";
    iconCanvas.setAttribute("aria-hidden", "true");
    btn.appendChild(iconCanvas);
    btn.addEventListener("click", openModal);
    document.body.appendChild(btn);
    launcherIconCleanup = attachJoystickIcon(btn, iconCanvas);
    launcherBtn = btn;
  };

  const removeButton = (): void => {
    if (!launcherBtn) return;
    if (launcherIconCleanup) {
      launcherIconCleanup();
      launcherIconCleanup = null;
    }
    launcherBtn.remove();
    launcherBtn = null;
  };

  const syncMq = (): void => {
    if (mq.matches && revealed) renderButton();
    else removeButton();
  };
  mq.addEventListener("change", syncMq);
  syncMq();

  let comboTimestamps: number[] = [];
  const onMagicKey = (e: KeyboardEvent): void => {
    if (e.code !== "Space") return;
    if (e.repeat) return;
    const target = e.target as Element | null;
    if (target) {
      const tag = target.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" ||
          (target as HTMLElement).isContentEditable) return;
    }
    if (modal || activeGame) return;

    const now = performance.now();
    comboTimestamps = comboTimestamps.filter((t) => now - t < COMBO_WINDOW_MS);
    comboTimestamps.push(now);
    if (comboTimestamps.length >= COMBO_PRESSES) {
      revealed = true;
      comboTimestamps = [];
      window.removeEventListener("keydown", onMagicKey);
      syncMq();
    }
  };
  window.addEventListener("keydown", onMagicKey);

  let modal: HTMLDivElement | null = null;
  let modalKeyHandler: ((e: KeyboardEvent) => void) | null = null;
  let modalLastFocused: HTMLElement | null = null;
  let modalFirstFocusable: HTMLElement | null = null;
  let modalLastFocusable: HTMLElement | null = null;

  let activeGame: Game | null = null;
  let host: HTMLDivElement | null = null;
  let gameCloseBtn: HTMLButtonElement | null = null;
  let gameEscHandler: ((e: KeyboardEvent) => void) | null = null;

  function openModal(): void {
    if (modal) return;
    modalLastFocused = document.activeElement as HTMLElement | null;

    const root = document.createElement("div");
    root.className = "games-modal";
    root.setAttribute("role", "dialog");
    root.setAttribute("aria-modal", "true");
    root.setAttribute("aria-labelledby", "games-modal-title");

    const card = document.createElement("div");
    card.className = "games-modal__card";

    const closeBtn = document.createElement("button");
    closeBtn.type = "button";
    closeBtn.className = "games-modal__close";
    closeBtn.setAttribute("aria-label", "Close");
    closeBtn.innerHTML = '<span aria-hidden="true">&times;</span>';
    closeBtn.addEventListener("click", closeModal);

    const title = document.createElement("h2");
    title.id = "games-modal-title";
    title.className = "games-modal__title";
    title.textContent = "Games";

    const list = document.createElement("ul");
    list.className = "games-modal__list";

    const focusable: HTMLElement[] = [];

    for (const game of games) {
      const li = document.createElement("li");
      const itemBtn = document.createElement("button");
      itemBtn.type = "button";
      itemBtn.className = "games-modal__item";
      const t = document.createElement("span");
      t.className = "games-modal__item-title";
      t.textContent = game.title;
      const d = document.createElement("span");
      d.className = "games-modal__item-desc";
      d.textContent = game.description;
      itemBtn.append(t, d);
      itemBtn.addEventListener("click", () => {
        closeModal();
        launchGame(game);
      });
      li.appendChild(itemBtn);
      list.appendChild(li);
      focusable.push(itemBtn);
    }
    focusable.push(closeBtn);

    card.append(closeBtn, title, list);
    root.appendChild(card);

    root.addEventListener("click", (e) => {
      if (e.target === root) closeModal();
    });

    modalFirstFocusable = focusable[0] ?? null;
    modalLastFocusable = focusable[focusable.length - 1] ?? null;

    modalKeyHandler = (e: KeyboardEvent): void => {
      if (e.key === "Escape") {
        e.preventDefault();
        closeModal();
        return;
      }
      if (e.key === "Tab" && modalFirstFocusable && modalLastFocusable) {
        if (e.shiftKey && document.activeElement === modalFirstFocusable) {
          e.preventDefault();
          modalLastFocusable.focus();
        } else if (!e.shiftKey && document.activeElement === modalLastFocusable) {
          e.preventDefault();
          modalFirstFocusable.focus();
        }
      }
    };
    window.addEventListener("keydown", modalKeyHandler);

    document.getElementById("main")?.setAttribute("inert", "");
    document.getElementById("nav")?.setAttribute("inert", "");

    document.body.appendChild(root);
    modal = root;
    modalFirstFocusable?.focus();
  }

  function closeModal(): void {
    if (!modal) return;
    modal.remove();
    modal = null;
    if (modalKeyHandler) {
      window.removeEventListener("keydown", modalKeyHandler);
      modalKeyHandler = null;
    }
    document.getElementById("main")?.removeAttribute("inert");
    document.getElementById("nav")?.removeAttribute("inert");
    modalLastFocused?.focus();
  }

  function launchGame(game: Game): void {
    if (activeGame) return;
    hooks.pause();
    document.body.classList.add("game-active");

    const main = document.getElementById("main");
    if (!main) {
      hooks.resume();
      document.body.classList.remove("game-active");
      return;
    }

    const hostEl = document.createElement("div");
    hostEl.className = "game-host";
    main.appendChild(hostEl);
    host = hostEl;

    const close = document.createElement("button");
    close.className = "game-close";
    close.type = "button";
    close.setAttribute("aria-label", "Exit game");
    close.innerHTML = '<span aria-hidden="true">&times;</span>';
    close.addEventListener("click", exitGame);
    document.body.appendChild(close);
    gameCloseBtn = close;

    gameEscHandler = (e: KeyboardEvent): void => {
      if (e.key === "Escape") {
        e.preventDefault();
        exitGame();
      }
    };
    window.addEventListener("keydown", gameEscHandler);

    activeGame = game;
    game.mount(hostEl);
  }

  function exitGame(): void {
    if (activeGame) {
      try { activeGame.unmount(); } catch { /* ignore */ }
      activeGame = null;
    }
    if (host) {
      host.remove();
      host = null;
    }
    if (gameCloseBtn) {
      gameCloseBtn.remove();
      gameCloseBtn = null;
    }
    if (gameEscHandler) {
      window.removeEventListener("keydown", gameEscHandler);
      gameEscHandler = null;
    }
    document.body.classList.remove("game-active");
    hooks.resume();
    launcherBtn?.focus();
  }
}

interface IconParticle {
  bx: number;
  by: number;
  phase: number;
  phase2: number;
  speed: number;
  drift: number;
  rise: number;
  life: number;
  size: number;
}

function attachJoystickIcon(
  btn: HTMLButtonElement,
  canvas: HTMLCanvasElement,
): () => void {
  const ctx = canvas.getContext("2d");
  if (!ctx) return () => {};

  const CANVAS_DISPLAY = 80;
  const ICON_DISPLAY = 28;
  const SCALE = Math.min(window.devicePixelRatio || 1, 2);
  const SIZE = CANVAS_DISPLAY * SCALE;
  const ICON_SIZE = ICON_DISPLAY * SCALE;
  const ICON_OFFSET = (SIZE - ICON_SIZE) / 2;

  canvas.width = SIZE;
  canvas.height = SIZE;

  const iconColorRaw = getComputedStyle(document.documentElement)
    .getPropertyValue("--fg")
    .trim();
  const iconColor = /^#[0-9a-f]{6}$/i.test(iconColorRaw) ? iconColorRaw : "#ececf1";

  const drawJoystick = (c: CanvasRenderingContext2D, fill: string, alpha: number): void => {
    c.save();
    c.globalAlpha = alpha;
    c.fillStyle = fill;
    const cx = SIZE / 2;
    const top = ICON_OFFSET;

    const ballR = ICON_SIZE * 0.16;
    const ballY = top + ICON_SIZE * 0.18;
    c.beginPath();
    c.arc(cx, ballY, ballR, 0, Math.PI * 2);
    c.fill();

    const shaftW = ICON_SIZE * 0.11;
    const shaftTop = ballY + ballR * 0.55;
    const shaftBottom = top + ICON_SIZE * 0.74;
    c.fillRect(cx - shaftW / 2, shaftTop, shaftW, shaftBottom - shaftTop);

    const baseRX = ICON_SIZE * 0.38;
    const baseRY = ICON_SIZE * 0.10;
    const baseY = top + ICON_SIZE * 0.80;
    c.beginPath();
    c.ellipse(cx, baseY, baseRX, baseRY, 0, 0, Math.PI * 2);
    c.fill();

    c.restore();
  };

  const off = document.createElement("canvas");
  off.width = SIZE;
  off.height = SIZE;
  const offCtx = off.getContext("2d");
  if (!offCtx) return () => {};
  drawJoystick(offCtx, "#ffffff", 1);
  const img = offCtx.getImageData(0, 0, SIZE, SIZE).data;

  const particles: IconParticle[] = [];
  for (let y = 0; y < SIZE; y += 1) {
    for (let x = 0; x < SIZE; x += 1) {
      const a = img[(y * SIZE + x) * 4 + 3]!;
      if (a > 128 && Math.random() < 0.55) {
        particles.push({
          bx: x,
          by: y,
          phase: Math.random() * Math.PI * 2,
          phase2: Math.random() * Math.PI * 2,
          speed: 0.6 + Math.random() * 1.4,
          drift: 0.5 + Math.random() * 1.5,
          rise: 0.4 + Math.random() * 1.2,
          life: Math.random(),
          size:
            Math.random() < 0.15
              ? 2 + Math.random() * 1.5
              : 0.8 + Math.random() * 1.2,
        });
      }
    }
  }

  const turb = (x: number, y: number, t: number, p: IconParticle): number => {
    const a =
      Math.sin(x * 0.06 + t * 1.1 + p.phase) +
      Math.cos(y * 0.05 - t * 1.3 + p.phase2);
    const b =
      Math.sin(x * 0.18 - t * 1.9 + p.phase2) * 0.6 +
      Math.cos(y * 0.21 + t * 1.7 + p.phase) * 0.6;
    return a + b;
  };

  const PUFF_SIZE = 64;
  const puff = document.createElement("canvas");
  puff.width = PUFF_SIZE;
  puff.height = PUFF_SIZE;
  const puffCtx = puff.getContext("2d");
  if (!puffCtx) return () => {};
  {
    const cx = PUFF_SIZE / 2;
    const ir = parseInt(iconColor.slice(1, 3), 16);
    const ig = parseInt(iconColor.slice(3, 5), 16);
    const ib = parseInt(iconColor.slice(5, 7), 16);
    const g = puffCtx.createRadialGradient(cx, cx, 0, cx, cx, cx);
    g.addColorStop(0, `rgba(${ir},${ig},${ib},1)`);
    g.addColorStop(0.6, `rgba(${ir},${ig},${ib},0.35)`);
    g.addColorStop(1, `rgba(${ir},${ig},${ib},0)`);
    puffCtx.fillStyle = g;
    puffCtx.fillRect(0, 0, PUFF_SIZE, PUFF_SIZE);
  }

  ctx.globalCompositeOperation = "lighter";
  ctx.globalAlpha = 0;
  ctx.drawImage(puff, 0, 0, 1, 1);
  ctx.globalAlpha = 1;
  ctx.globalCompositeOperation = "source-over";
  ctx.clearRect(0, 0, SIZE, SIZE);

  let hovered = false;
  let t = 0;
  let time = 0;
  let last = performance.now();
  let rafId: number | null = null;

  const onEnter = (): void => { hovered = true; };
  const onLeave = (): void => { hovered = false; };
  // Only treat focus as "hovered" when it's a visible (keyboard) focus.
  // Programmatic focus — like launcherBtn?.focus() after exiting a game —
  // would otherwise lock the cloud animation on.
  const onFocus = (): void => {
    if (btn.matches(":focus-visible")) hovered = true;
  };
  btn.addEventListener("mouseenter", onEnter);
  btn.addEventListener("mouseleave", onLeave);
  btn.addEventListener("focus", onFocus);
  btn.addEventListener("blur", onLeave);

  // Clicking the button puts focus on it; bfcache then freezes the page with
  // focus still on the button, so on restore the cloud keeps playing.
  // pagehide reset before freeze; pageshow reset on restore; blur the
  // button in both cases so a leftover focus doesn't re-trigger the effect.
  const resetState = (): void => {
    hovered = false;
    t = 0;
    last = performance.now();
    if (document.activeElement === btn) btn.blur();
  };
  window.addEventListener("pagehide", resetState);
  window.addEventListener("pageshow", resetState);

  const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (reduced) {
    drawJoystick(ctx, iconColor, 1);
    return () => {
      btn.removeEventListener("mouseenter", onEnter);
      btn.removeEventListener("mouseleave", onLeave);
      btn.removeEventListener("focus", onFocus);
      btn.removeEventListener("blur", onLeave);
      window.removeEventListener("pagehide", resetState);
      window.removeEventListener("pageshow", resetState);
    };
  }

  const frame = (now: number): void => {
    const dt = Math.min(0.05, (now - last) / 1000);
    last = now;
    time += dt;

    const target = hovered ? 1 : 0;
    t += (target - t) * 0.1;

    ctx.clearRect(0, 0, SIZE, SIZE);

    if (t < 0.995) {
      drawJoystick(ctx, iconColor, Math.pow(1 - t, 1.4));
    }

    if (t > 0.005) {
      ctx.globalCompositeOperation = "lighter";
      const ampBase = Math.pow(t, 1.2) * SCALE * 6.0;

      for (let i = 0; i < particles.length; i++) {
        const p = particles[i]!;
        const nx = turb(p.bx, p.by, time * p.speed, p);
        const ny = turb(p.bx + 53.1, p.by + 17.7, time * p.speed * 0.9, p);
        const amp = ampBase * p.drift;
        const rise = -Math.pow(t, 1.4) * 4 * p.rise;
        const x = p.bx + nx * amp;
        const y = p.by + ny * amp + rise;
        const flicker = 0.55 + 0.45 * Math.sin(time * 2.1 * p.speed + p.life * 6.28);
        const dist = Math.hypot(x - p.bx, y - p.by);
        const falloff = Math.max(0, 1 - dist / (SCALE * 14));
        const a = t * flicker * (0.18 + 0.55 * falloff);
        if (a <= 0.01) continue;
        const r = p.size * SCALE;
        const w = r * 2;
        ctx.globalAlpha = a;
        ctx.drawImage(puff, x - r, y - r, w, w);
      }
      ctx.globalAlpha = 1;
      ctx.globalCompositeOperation = "source-over";
    }

    rafId = requestAnimationFrame(frame);
  };
  rafId = requestAnimationFrame(frame);

  return () => {
    if (rafId !== null) cancelAnimationFrame(rafId);
    rafId = null;
    btn.removeEventListener("mouseenter", onEnter);
    btn.removeEventListener("mouseleave", onLeave);
    btn.removeEventListener("focus", onFocus);
    btn.removeEventListener("blur", onLeave);
    window.removeEventListener("pagehide", resetState);
    window.removeEventListener("pageshow", resetState);
  };
}
